import React from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useRegister } from '@/hooks/use-api';
import { registerSchema, type RegisterFormData } from '@/lib/validations';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import logo from '/onextel-logo-white.png';

const RegisterPage: React.FC = () => {
  const registerMutation = useRegister();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema as any),
    defaultValues: { name: '', company: '', email: '', password: '' },
  });

  const onSubmit = async (values: RegisterFormData) => {
    toast.loading('Creating account...', { id: 'register-toast' });
    try {
      await registerMutation.mutateAsync(values);
    } catch (error: any) {
      console.error('Registration error:', error);
      // Always dismiss the loading toast so the page never stays stuck
      toast.dismiss('register-toast');

      if (error.response?.data?.errors) {
        // Field-level validation errors from backend
        error.response.data.errors.forEach((err: any) => {
          form.setError(err.field as keyof RegisterFormData, {
            message: err.message,
          });
        });
      } else if (error.response?.data?.code === 'EMAIL_ALREADY_EXISTS') {
        // Duplicate email — set inline field error so user knows exactly what to fix
        form.setError('email', {
          message: 'An account with this email already exists.',
        });
      }
      // Generic toast is handled by useRegister onError — no double toast needed
    }
  };

  return (
    <main className="login-bg-container">
      <section className="dark:bg-main-dark-bg relative">
        <div className="flex flex-col items-center justify-center h-screen px-6 mx-auto">
          <div className="w-full md:mt-0 max-w-screen-lg">
            <Card className="glassy-card flex flex-row w-full">
              {/* Left branding panel */}
              <div className="w-full md:w-1/2 hidden md:flex flex-col justify-center items-center p-12 bg-gradient-to-br from-red-500 to-red-600 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-400 rounded-full opacity-20 -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-red-400 rounded-full opacity-20 -ml-12 -mb-12"></div>
                <div className="flex flex-col justify-center items-center text-center z-10 max-w-sm">
                  <img className="w-32 mb-8" src={logo} alt="onextel-logo" />
                  <h1 className="text-white font-bold text-3xl mb-4">
                    Start your SMS journey.
                  </h1>
                  <p className="text-white/90 text-sm mb-8 leading-relaxed">
                    Join thousands of businesses using intelligent SMS campaigns
                    to connect with customers and drive growth.
                  </p>
                  <div className="space-y-4 w-full">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                      </div>
                      <span className="text-sm">Team collaboration</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <span className="text-sm">Campaign scheduling</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                        </svg>
                      </div>
                      <span className="text-sm">Real-time analytics</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right form panel */}
              <div className="w-full md:w-1/2">
                <div className="auth-form-content">
                  <div className="text-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      Create Account
                    </h2>
                    <p className="text-sm text-gray-600">
                      Join Onextel - Optimove and start your SMS journey
                    </p>
                  </div>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter your name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">
                              Company Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your company name"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">Email</FormLabel>
                            <FormControl>
                              <Input placeholder="you@company.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem className="auth-field">
                            <FormLabel className="text-black">
                              Password
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="password"
                                placeholder="Enter your password"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        className="w-full bg-red-500 hover:bg-[#23203f] text-white font-medium"
                        type="submit"
                        disabled={registerMutation.isPending}
                      >
                        {registerMutation.isPending
                          ? 'Creating Account...'
                          : 'Create Account'}
                      </Button>
                      <div className="auth-divider">or</div>
                      <div className="flex flex-col space-y-2">
                        <Link className="w-full" to="/login">
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-600 hover:bg-[#23203f] hover:text-white hover:border-[#23203f] transition-colors"
                          >
                            Back to Login
                          </Button>
                        </Link>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RegisterPage;
